import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { DraggableModal } from './ui/draggable-modal';
import { RefreshCw } from 'lucide-react';

const fmtDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR');
};

export default function ActiveDelegationsCard({
  activeSubstitutions = [],
  title = '🔄 Aktif Vekaletler',
  className = '',
  personnel = [],
  regions = [],
  parks = []
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const raw = Array.isArray(activeSubstitutions) ? activeSubstitutions : [];
  // Aynı (leave_request_id, asil) için tek kart göster
  const seen = new Set();
  const list = raw.filter((sub) => {
    const key = `${sub?.leave_request_id || ''}_${sub?.original_personnel_id || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const getSubstituteName = (sub) => {
    if (sub?.substitute_personnel_name) return sub.substitute_personnel_name;
    if (sub?.substitute_personnel_id && Array.isArray(personnel)) {
      const p = personnel.find((x) => x.id === sub.substitute_personnel_id);
      if (p?.name) return p.name;
    }
    return '-';
  };

  const getOriginalName = (sub) => {
    if (sub?.original_personnel_name) return sub.original_personnel_name;
    if (sub?.original_personnel_id && Array.isArray(personnel)) {
      const p = personnel.find((x) => x.id === sub.original_personnel_id);
      if (p?.name) return p.name;
    }
    return '-';
  };

  // Park başına vekil adını çöz (backend substitute_breakdown girdisi için)
  const getBreakdownSubName = (entry) => {
    if (entry?.substitute_personnel_name) return entry.substitute_personnel_name;
    if (entry?.substitute_personnel_id && Array.isArray(personnel)) {
      const p = personnel.find((x) => x.id === entry.substitute_personnel_id);
      if (p?.name) return p.name;
    }
    return 'Atanmadı';
  };

  const normalizeRole = (role) => String(role || '').trim().toLowerCase();
  const isRegionBasedRole = (role) =>
    ['formen', 'bolge_muhendisi', 'mudur_yardimcisi'].includes(normalizeRole(role));

  const getDelegationRegionNames = (sub) => {
    const names = [];
    const seen = new Set();
    const regionNameById = new Map(
      (Array.isArray(regions) ? regions : [])
        .map((r) => [String(r?.id || '').trim(), String(r?.name || '').trim()])
        .filter(([id, name]) => id && name)
    );
    const parkById = new Map(
      (Array.isArray(parks) ? parks : [])
        .map((p) => [String(p?.id || '').trim(), p])
        .filter(([id]) => id)
    );
    const addRegion = (rid) => {
      const key = String(rid || '').trim();
      if (!key || seen.has(key)) return;
      const regionName = regionNameById.get(key);
      if (!regionName) return;
      seen.add(key);
      names.push(regionName);
    };

    const originalId = String(sub?.original_personnel_id || '').trim();
    const originalPerson = (Array.isArray(personnel) ? personnel : []).find(
      (p) => String(p?.id || '').trim() === originalId
    );

    (Array.isArray(originalPerson?.region_ids) ? originalPerson.region_ids : []).forEach(addRegion);
    addRegion(originalPerson?.region_id);
    addRegion(originalPerson?.formen_region_id);

    if (originalId) {
      (Array.isArray(regions) ? regions : []).forEach((r) => {
        if (String(r?.assigned_bolge_muhendisi_id || '').trim() === originalId) addRegion(r?.id);
        const formenIds = Array.isArray(r?.assigned_formen_ids) ? r.assigned_formen_ids : [];
        if (formenIds.some((fid) => String(fid || '').trim() === originalId)) addRegion(r?.id);
      });
    }

    (Array.isArray(sub?.parks) ? sub.parks : []).forEach((sp) => {
      addRegion(sp?.region_id);
      const pid = String(sp?.id || sp?.park_id || '').trim();
      if (pid) addRegion(parkById.get(pid)?.region_id);
    });

    return names;
  };

  return (
    <>
      <Card
        className={`bg-gradient-to-br from-indigo-400 to-indigo-500 border-indigo-600 cursor-pointer transition-transform hover:scale-105 hover:shadow-lg ${className}`}
        onClick={() => setDetailOpen(true)}
      >
        <CardContent className="p-4">
          <div className="flex flex-col items-center text-center">
            <RefreshCw className="h-8 w-8 text-white mb-2" />
            <p className="text-2xl font-bold text-white">{list.length}</p>
            <p className="text-xs text-indigo-50 font-semibold">Aktif Vekalet</p>
          </div>
        </CardContent>
      </Card>

      <DraggableModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={title}
        subtitle={`Toplam ${list.length} aktif vekalet`}
        maxWidth="max-w-3xl"
      >
        <div className="space-y-4">
          {list.length === 0 && (
            <p className="text-center text-gray-500 py-8">Aktif vekalet bulunmuyor</p>
          )}

          {list
            .slice()
            .sort((a, b) => (a?.original_personnel_name || '').localeCompare(b?.original_personnel_name || '', 'tr'))
            .map((sub) => {
              const parks = Array.isArray(sub?.parks) ? sub.parks : [];
              const originalPerson = Array.isArray(personnel)
                ? personnel.find((p) => p?.id === sub?.original_personnel_id)
                : null;
              const regionNames = getDelegationRegionNames(sub);
              const showRegions = isRegionBasedRole(originalPerson?.role);
              // Park başına farklı vekil varsa (park vekil planı), dağılımı göster
              const breakdown = Array.isArray(sub?.substitute_breakdown) ? sub.substitute_breakdown : [];
              const distinctSubIds = new Set(
                breakdown
                  .map((b) => String(b?.substitute_personnel_id || b?.substitute_personnel_name || '').trim())
                  .filter(Boolean)
              );
              const isPerPark = !showRegions && breakdown.length > 0 && distinctSubIds.size > 1;
              return (
                <Card
                  key={sub?.id || `${sub?.original_personnel_id}-${sub?.substitute_personnel_id}`}
                  className="border-l-4 border-l-indigo-500"
                >
                  <CardContent className="p-4">
                    {isPerPark ? (
                      <>
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <p className="text-xs text-blue-600 font-semibold mb-1">ASIL PERSONEL</p>
                          <p className="font-bold text-blue-800">{getOriginalName(sub)}</p>
                          <div className="mt-2 text-xs text-blue-700">
                            📅 {fmtDate(sub?.start_date)} - {fmtDate(sub?.end_date)}
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-gray-600 mb-2">Vekalet Dağılımı (Park → Vekil):</p>
                          <div className="space-y-1">
                            {breakdown.map((b, i) => (
                              <div
                                key={b?.id || b?.name || i}
                                className="flex items-center justify-between gap-2 text-sm bg-gray-50 rounded px-2 py-1.5 border border-gray-200"
                              >
                                <span className="font-medium text-green-700">{b?.name || 'İsimsiz Park'}</span>
                                <span className="text-gray-400">→</span>
                                <span className="font-semibold text-orange-700">{getBreakdownSubName(b)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <p className="text-xs text-blue-600 font-semibold mb-1">ASIL PERSONEL</p>
                            <p className="font-bold text-blue-800">
                              {getOriginalName(sub)}
                            </p>
                            <div className="mt-2 text-xs text-blue-700">
                              📅 {fmtDate(sub?.start_date)} - {fmtDate(sub?.end_date)}
                            </div>
                          </div>

                          <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                            <p className="text-xs text-orange-600 font-semibold mb-1">VEKİL</p>
                            <p className="font-bold text-orange-800">
                              {getSubstituteName(sub)}
                            </p>
                          </div>
                        </div>

                        {(showRegions || parks.length > 0) && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-600 mb-2">
                              {showRegions ? 'Vekalet Bölgeleri:' : 'Vekalet Parkları:'}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {showRegions
                                ? (regionNames.length > 0 ? regionNames : ['Bölge bilgisi bulunamadı']).map((name) => (
                                    <Badge
                                      key={name}
                                      variant="outline"
                                      className="text-xs bg-blue-50 border-blue-300 text-blue-700"
                                    >
                                      {name}
                                    </Badge>
                                  ))
                                : parks.map((park) => (
                                    <Badge
                                      key={park?.id || park?.name}
                                      variant="outline"
                                      className="text-xs bg-green-50 border-green-300 text-green-700"
                                    >
                                      {park?.name || 'İsimsiz Park'}
                                    </Badge>
                                  ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
        </div>
      </DraggableModal>
    </>
  );
}
